import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { Text } from '../Text/index.ts';
import { RatingStar } from './RatingStar.tsx';

const ratingI18n = {
  5: defineMessage({
    id: 'ui:rating.excellent',
    defaultMessage: 'Excellent'
  }),
  4: defineMessage({
    id: 'ui:rating.great',
    defaultMessage: 'Very good'
  }),
  3: defineMessage({
    id: 'ui:rating.ok',
    defaultMessage: 'Average'
  }),
  2: defineMessage({
    id: 'ui:rating.poor',
    defaultMessage: 'Poor'
  }),
  1: defineMessage({
    id: 'ui:rating.terrible',
    defaultMessage: 'Terrible'
  })
};

export function EditableRating({
  disabled = false,
  onChange,
  size = 'md',
  showText = true,
  value
}: {
  disabled?: boolean;
  onChange: (value: number) => void;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
  value: number;
}) {
  const [hoveredValue, setHoveredValue] = useState<number>(0);

  const text = hoveredValue || value;

  return (
    <div className="flex items-center">
      <div
        className={clsx(
          'grid grid-cols-5 grid-rows-1 text-primary-600',
          size === 'sm' && 'w-36',
          size === 'md' && 'w-52',
          size === 'lg' && 'w-72',
          disabled && 'opacity-50'
        )}
      >
        {useMemo(
          () =>
            [...Array(5)]
              .map((_, i) => i + 1)
              .map(starValue => (
                <RatingStar
                  key={starValue}
                  className={clsx('size-full cursor-pointer', disabled && 'pointer-events-none')}
                  onMouseEnter={() => setHoveredValue(starValue)}
                  onMouseLeave={() => setHoveredValue(0)}
                  onClick={() => onChange(value !== starValue ? starValue : 0)}
                  state={
                    (hoveredValue && hoveredValue >= starValue) ||
                    (!hoveredValue && value >= starValue)
                      ? 'full'
                      : 'empty'
                  }
                />
              )),
          [disabled, onChange, hoveredValue, value]
        )}
      </div>

      {showText && (
        <span className="ml-4">
          {text > 0 ? (
            <Text block className="text-primary-700" semibold>
              <FormattedMessage {...ratingI18n[text as keyof typeof ratingI18n]} />
            </Text>
          ) : undefined}
        </span>
      )}
    </div>
  );
}
